import React, { useState } from 'react';

const TIME_OPTIONS = [
  { value: '', label: 'All Time' },
  { value: '7', label: 'Last 7 Days' },
  { value: '14', label: 'Last 14 Days' },
  { value: '30', label: 'Last 30 Days' },
  { value: '90', label: 'Last 90 Days' },
  { value: 'custom', label: 'Custom Dates…' },
];

const STEP_OPTIONS = [
  { value: '', label: 'All Steps' },
  { value: 'quote', label: 'Requested Quote' },
  { value: 'checkout', label: 'Reached Checkout' },
  { value: 'analysis', label: 'Reached Analysis' },
  { value: 'business', label: 'Reached Business' },
  { value: 'start', label: 'Started Only' },
];

const SOURCE_OPTIONS = [
  { value: '', label: 'All Sources' },
  { value: 'google_ads', label: 'Google Ads' },
  { value: 'google_organic', label: 'Google Organic' },
  { value: 'direct', label: 'Direct' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'linkedin', label: 'LinkedIn' },
];

// value formats: '' any · 'N' quiet ≥N days · 'N-M' active between N and M days ago
const ACTIVITY_OPTIONS = [
  { value: '', label: 'Any Activity' },
  { value: '2', label: 'Quiet 2+ Days' },
  { value: '3', label: 'Quiet 3+ Days' },
  { value: '7', label: 'Quiet 7+ Days' },
  { value: '14', label: 'Quiet 14+ Days' },
  { value: '30', label: 'Quiet 30+ Days' },
  { value: '30-60', label: '30–60 Days Ago' },
  { value: '60-90', label: '60–90 Days Ago' },
  { value: 'custom', label: 'Custom Range…' },
];

function PipelineFilters({ filters, onChange }) {
  const [timeMode, setTimeMode] = useState(filters.startDate ? 'custom' : (filters.days || ''));
  const [activityMode, setActivityMode] = useState(() => {
    if (filters.quietDays && filters.activeWithinDays) {
      const preset = `${filters.quietDays}-${filters.activeWithinDays}`;
      return ACTIVITY_OPTIONS.some((o) => o.value === preset) ? preset : 'custom';
    }
    return filters.quietDays || '';
  });
  const [customFrom, setCustomFrom] = useState(filters.quietDays || '');
  const [customTo, setCustomTo] = useState(filters.activeWithinDays || '');

  const apply = (patch, removals = []) => {
    const next = { ...filters };
    removals.forEach((k) => delete next[k]);
    Object.entries(patch).forEach(([k, v]) => {
      if (v) next[k] = v;
      else delete next[k];
    });
    onChange(next);
  };

  const handleChange = (key, value) => apply({ [key]: value });

  const handleTimeMode = (value) => {
    setTimeMode(value);
    if (value === 'custom') {
      // Wait for both dates before applying
      apply({}, ['days']);
    } else {
      apply({ days: value }, ['startDate', 'endDate']);
    }
  };

  const handleCustomDates = (which, value) => {
    const startDate = which === 'start' ? value : filters.startDate;
    const endDate = which === 'end' ? value : filters.endDate;
    apply({ startDate, endDate }, ['days']);
  };

  const handleActivityMode = (value) => {
    setActivityMode(value);
    if (value === 'custom') {
      if (customFrom && customTo) {
        apply({ quietDays: customFrom, activeWithinDays: customTo });
      }
      return;
    }
    if (value.includes('-')) {
      const [from, to] = value.split('-');
      apply({ quietDays: from, activeWithinDays: to });
    } else {
      apply({ quietDays: value }, ['activeWithinDays']);
    }
  };

  const handleCustomActivity = (which, value) => {
    const from = which === 'from' ? value : customFrom;
    const to = which === 'to' ? value : customTo;
    if (which === 'from') setCustomFrom(value);
    else setCustomTo(value);
    // Apply once at least the lower bound is set; upper bound optional
    if (from) {
      apply({ quietDays: from, activeWithinDays: to || '' });
    }
  };

  return (
    <div className="mkt-filters">
      <div className="mkt-filter-group">
        <label className="mkt-filter-label">Time</label>
        <select
          className="mkt-filter-select"
          value={timeMode}
          onChange={(e) => handleTimeMode(e.target.value)}
        >
          {TIME_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      {timeMode === 'custom' && (
        <div className="mkt-filter-group">
          <label className="mkt-filter-label">Created Between</label>
          <div className="mkt-filter-inline">
            <input
              type="date"
              className="mkt-date-input"
              value={filters.startDate || ''}
              onChange={(e) => handleCustomDates('start', e.target.value)}
            />
            <span>–</span>
            <input
              type="date"
              className="mkt-date-input"
              value={filters.endDate || ''}
              onChange={(e) => handleCustomDates('end', e.target.value)}
            />
          </div>
        </div>
      )}
      <div className="mkt-filter-group">
        <label className="mkt-filter-label">Wizard Step</label>
        <select
          className="mkt-filter-select"
          value={filters.maxStep || ''}
          onChange={(e) => handleChange('maxStep', e.target.value)}
        >
          {STEP_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div className="mkt-filter-group">
        <label className="mkt-filter-label">Source</label>
        <select
          className="mkt-filter-select"
          value={filters.source || ''}
          onChange={(e) => handleChange('source', e.target.value)}
        >
          {SOURCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div className="mkt-filter-group">
        <label className="mkt-filter-label">Last Activity</label>
        <select
          className="mkt-filter-select"
          value={activityMode}
          onChange={(e) => handleActivityMode(e.target.value)}
        >
          {ACTIVITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      {activityMode === 'custom' && (
        <div className="mkt-filter-group">
          <label className="mkt-filter-label">Between (Days Ago)</label>
          <div className="mkt-filter-inline">
            <input
              type="number"
              min="0"
              placeholder="30"
              className="mkt-days-input"
              value={customFrom}
              onChange={(e) => handleCustomActivity('from', e.target.value)}
              title="Last activity at least this many days ago"
            />
            <span>–</span>
            <input
              type="number"
              min="0"
              placeholder="60"
              className="mkt-days-input"
              value={customTo}
              onChange={(e) => handleCustomActivity('to', e.target.value)}
              title="Last activity at most this many days ago"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default PipelineFilters;
